import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../../components/Layout/MainLayout';
import { DownloadIcon, BackIcon, UploadIcon } from '../../../components/Dashboard/DataCenterIcons';
import '../../../assets/styles/RDEDownloadsPage.css';
import '../../../assets/styles/FDCAppendReuploadPage.css'; // reuse upload styles
import { ROUTES } from '../../../utils/constants';
import commonService from '../../../services/commonService';
import Papa from "papaparse";

const RDEDownloadsPage: React.FC = () => {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState<any>({
    data_type: 'b2b',
    industry: [],
    job_title: [],
    job_level_name: [],
    employee_size_range: [],
    revenue_range: [],
    sic_code: [],
    naics_code: [],
    city: [],
    zipcode: [],
    state: [],
    country: [],
    technology_name: [],
    company_name: [],
    company_website: [],
  });

  /* ---------- File handling ---------- */

  const processFile = (file: File) => {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Only CSV files allowed');
      return;
    }

    setSelectedFile(file);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: Papa.ParseResult<any>) => {
        const parsedRows = results.data as any[];

        const updatedFilters: any = {};
        Object.keys(filters).forEach(key => {
          updatedFilters[key] = key === "data_type" ? "b2b" : [];
        });

        parsedRows.forEach(row => {
          Object.keys(row).forEach(key => {
            if (updatedFilters.hasOwnProperty(key)) {
              const val = row[key]?.toString().trim();
              if (val) updatedFilters[key].push(val);
            }
          });
        });

        setFilters(updatedFilters);
      },
    });
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      processFile(e.target.files[0]);
    }
  };

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) {
      processFile(e.dataTransfer.files[0]);
    }
  }, []);

  const clearFile = () => {
    setSelectedFile(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  /* ---------- Search ---------- */

  const handleSearch = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const results = await commonService.rdcSearch(filters);
      navigate(ROUTES.RDC_COUNT, { state: { filters, results } });
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------- Template ---------- */

  const handleDownloadTemplate = () => {
    const headers = [
      "company_website",
      "company_name",
      "industry",
      "job_title",
      "job_level_name",
      "employee_size_range",
      "revenue_range",
      "sic_code",
      "naics_code",
      "city",
      "zipcode",
      "state",
      "country",
      "technology_name"
    ];

    const blob = new Blob([headers.join(",")], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "rde_template.csv";
    link.click();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <MainLayout>
      <div className="fdc-append-reupload-page" style={{ backgroundColor: '#f0f8ff' }}>

        {/* Header */}
        <div className="rde-header">
          <button onClick={() => navigate(ROUTES.RDC)} className="rde-back-button">
            <BackIcon /> Back
          </button>

          {/* <h1 className="rde-title">RDE Downloads</h1> */}

          <button onClick={handleDownloadTemplate} className="rde-download-template-button">
            <DownloadIcon /> Download template
          </button>
        </div>

        {/* Upload Section (NEW DESIGN) */}
        <div className="upload-container">
          <div className="upload-card">
            <h4 className='mb-3'>Upload CSV File(Data Enrichment)</h4>

            <div
              className={`file-input-area ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".csv"
                hidden
                onChange={onFileChange}
                disabled={isLoading}
              />

              <div className="upload-placeholder">
                <UploadIcon />
                <p>{selectedFile ? selectedFile.name : 'Click to browse or drag & drop CSV'}</p>
                <p className="upload-hint">Supports CSV files • Max 10MB</p>
              </div>
            </div>

            {/* Selected File */}
            {selectedFile && (
              <div className="selected-file-card">
                <div className="file-info">
                  <span className="file-name">{selectedFile.name}</span>
                  <span className="file-size" style={{ textAlign: 'left' }}>
                    {formatFileSize(selectedFile.size)}
                  </span>
                </div>
                <button className="remove-file-btn" onClick={clearFile} disabled={isLoading}>
                  ×
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="action-buttons">
              <button
                className="btn-secondary"
                onClick={clearFile}
                disabled={!selectedFile || isLoading}
              >
                Clear
              </button>

              <button
                className="btn-primary"
                onClick={handleSearch}
                disabled={!selectedFile || isLoading}
              >
                {isLoading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="rde-status-section">
          <button
            onClick={() => navigate(ROUTES.RDC_STATUS)}
            className="rde-status-button"
          >
            Check Status of Your Download
          </button>
        </div>

      </div>
    </MainLayout>
  );
};

export default RDEDownloadsPage;
